/** @jsxImportSource @emotion/react */

import { useMutation } from '@apollo/client'
import { Button, Typography } from '@mui/material'
import { FormApi } from 'final-form'
import { useState } from 'react'
import { Form as FinalForm } from 'react-final-form'
import {
  updateVideoReviewSection,
  updateVideoReviewSectionVariables,
} from '__generatedTypes__/updateVideoReviewSection'
import { InputField } from 'components/Input'
import { UPDATE_VIDEO_REVIEW_SECTION } from 'queries/videoReview'
import { toast } from 'utils/toast'
import * as classes from './styles'

type SectionValues = {
  title: string
  description: string
}

type Props = {
  section: SectionValues & { id: string }
}

const validateRequiredTrimmed = (value?: string) => (value?.trim() ? undefined : 'Required')

export const ReviewSectionForm = ({ section }: Props) => {
  const [updateSection] = useMutation<updateVideoReviewSection, updateVideoReviewSectionVariables>(
    UPDATE_VIDEO_REVIEW_SECTION
  )
  // Seed once on mount — parent uses network-only so this is fresh server data.
  const [initialValues] = useState(() => ({
    title: section.title,
    description: section.description,
  }))

  const handleSubmit = async (values: SectionValues, form: FormApi<SectionValues>) => {
    const nextValues = {
      title: values.title.trim(),
      description: values.description.trim(),
    }

    try {
      const response = await updateSection({
        variables: {
          updateVideoReviewSectionInputFieldName: {
            id: section.id,
            ...nextValues,
          },
        },
      })
      const updatedSection = response.data?.updateVideoReviewSection
      const updatedValues = {
        title: updatedSection?.title ?? nextValues.title,
        description: updatedSection?.description ?? nextValues.description,
      }

      form.restart(updatedValues)
      toast.success('Review video section updated successfully')
    } catch (error) {
      toast.error('Failed to update review video section')
    }
  }

  return (
    <FinalForm<SectionValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      render={({ form, handleSubmit, pristine, submitting }) => (
        <form css={classes.sectionForm} onSubmit={handleSubmit}>
          <Typography variant="body1" css={classes.sectionFormTitle}>
            Review Video Section
          </Typography>

          <div css={classes.sectionFields}>
            <InputField
              name="title"
              validate={validateRequiredTrimmed}
              label="Title"
              placeholder="Enter title"
              fullWidth
              disabled={submitting}
            />
            <InputField
              name="description"
              validate={validateRequiredTrimmed}
              label="Description"
              placeholder="Enter description"
              fullWidth
              multiline
              minRows={3}
              disabled={submitting}
            />
          </div>

          <div css={classes.sectionActions}>
            <Button
              color="blue"
              variant="outlined"
              size="large"
              type="button"
              onClick={() => form.restart()}
              disabled={submitting || pristine}
            >
              Cancel
            </Button>
            <Button color="blue" variant="contained" size="large" type="submit" disabled={submitting || pristine}>
              {submitting ? 'Updating...' : 'Update'}
            </Button>
          </div>
        </form>
      )}
    />
  )
}
